import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Replace } from 'lucide-react';
import { usePromptVersions, useRevertToPromptVersion } from '@/api/queries/prompts';
import type { PromptVersionHistoryItem } from '@/types/prompt';
import { PromptStatusEnum } from '@/types/prompt';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface VersionHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  promptId: string;
}

export function VersionHistoryDialog({ open, onOpenChange, promptId }: VersionHistoryDialogProps) {
  const { t } = useTranslation('prompts');
  const { t: tCommon } = useTranslation('common');
  const { data, isLoading, error } = usePromptVersions(promptId, open);
  const revertToVersion = useRevertToPromptVersion();

  const handleRevertToVersion = async (documentId: string, versionNumber: number) => {
    if (window.confirm(t('messages.confirmRevert', { versionNumber }))) {
      revertToVersion.mutate({ documentId, versionNumber });
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case PromptStatusEnum.ACTIVE.valueOf():
        return 'default';
      case PromptStatusEnum.DRAFT.valueOf():
        return 'secondary';
      case PromptStatusEnum.ARCHIVED.valueOf():
        return 'outline';
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('versionHistory', { promptId })}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {isLoading && <div className="text-center py-8">{t('messages.loadingVersions')}</div>}

          {error && (
            <div className="text-center text-red-600 py-8">
              {t('messages.errorLoadingVersions', {
                message: error instanceof Error ? error.message : 'Unknown error',
              })}
            </div>
          )}

          {data?.versions && data.versions.length === 0 && (
            <div className="text-center text-gray-500 py-8">{t('messages.noVersionsFound')}</div>
          )}

          {data?.versions?.map((versionItem: PromptVersionHistoryItem) => {
            const prompt = versionItem.document_state;
            return (
              <Card key={prompt._id || prompt.prompt_id} className="relative">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold">{prompt.name}</h3>
                        <Badge variant={getStatusBadgeVariant(prompt.status)}>
                          {prompt.status}
                        </Badge>
                        <Badge variant="outline">v{versionItem.version_number}</Badge>
                        <Badge variant="secondary">{versionItem.operation_type}</Badge>
                        {versionItem.version_number ===
                          Math.max(...(data.versions?.map(v => v.version_number) || [])) && (
                          <Badge
                            variant="default"
                            className="bg-green-100 text-green-800 border-green-300"
                          >
                            {t('latest')}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{prompt.description}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>
                          {t('versionCreated')}: {new Date(versionItem.updated_at).toLocaleString()}
                        </span>
                        <span>
                          {t('by')}: {versionItem.updated_by}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {versionItem.version_number !==
                        Math.max(...(data.versions?.map(v => v.version_number) || [])) && (
                        <Tooltip>
                          <TooltipTrigger>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                prompt._id &&
                                handleRevertToVersion(prompt._id, versionItem.version_number)
                              }
                              className="text-red-600 hover:text-red-700"
                            >
                              <Replace className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{t('revertToThisVersion')}</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {prompt.query_keywords.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-gray-700">{t('keywords')}: </span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {prompt.query_keywords.map((keyword, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {keyword}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {prompt.required_tools.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-gray-700">
                          {t('requiredTools')}:{' '}
                        </span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {prompt.required_tools.map((tool, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {tool}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="flex justify-end">
          <Button onClick={() => onOpenChange(false)}>{tCommon('buttons.close')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
